import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy} from '@angular/core';
import {createSelector, Store} from '@ngrx/store';
import {MatCard, MatCardContent, MatCardTitle} from '@angular/material/card';
import {InputFormFieldComponent} from '@aviary/forms/fields/input-form-field/input-form-field.component';
import {ReactiveFormsModule} from '@angular/forms';
import {FormRowComponent} from '@aviary/forms/fields/form-row/form-row.component';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {SelectFormFieldComponent} from '@aviary/forms/fields/select-form-field/select-form-field.component';
import {STATE_SELECT} from '@aviary/store/common/common.types';
import {CheckboxFormFieldComponent} from '@aviary/forms/fields/checkbox-form-field/checkbox-form-field.component';
import {buildFormGroup, hasChanged, HasChangedFunction} from '@aviary/forms/form.helpers';
import {PatronValidators} from '@store/patron/patron.validators';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {MxSelector} from '@aviary/store/store.types';
import {HiddenUntilLoadedDirective} from '@aviary/directives/hidden-until-loaded.directive';
import {PatronActions} from '@store/patron/patron.actions';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {LibraryCardComponent} from '@lib/components/library-card/library-card.component';
import {PatronAddressDisplayComponent} from '@app/my-account/my-profile/address-display/patron-address-display.component';
import {TruncateDirective} from '@aviary/directives/truncate.directive';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS, STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS} from '@store/patron/patron.types';
import {NgTemplateOutlet} from '@angular/common';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {VERIFICATION_EMAIL_RESEND_COOLDOWN_MS} from '@store/registration/registration.types';

@Component({
  selector: 'px-my-profile',
  templateUrl: './my-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardTitle, MatCard, MatCardContent, InputFormFieldComponent, ReactiveFormsModule, FormRowComponent, SelectFormFieldComponent, CheckboxFormFieldComponent,
    SelectPipe, HiddenUntilLoadedDirective, LibraryCardComponent, PatronAddressDisplayComponent, TruncateDirective, FormatPipe, NgTemplateOutlet,
    TextButtonComponent, ActionButtonsComponent, FilledButtonComponent
  ]
})
export class MyProfileComponent implements OnDestroy {
  private readonly store: Store = inject(Store);
  private readonly formBuilder: ExtendedFormBuilder = inject(ExtendedFormBuilder);
  private readonly validators = inject(PatronValidators).editPatronInfo;
  public readonly currentPatronSelectors = PatronSelectors.Auth;
  public readonly statelessSelectors = PatronSelectors.Stateless;
  public readonly referenceOptionSelectors = PatronSelectors.ReferenceOptions;
  public readonly viewportSelectors = ViewportSelectors;
  public readonly STATE_SELECT = STATE_SELECT;
  public readonly hasChanged: HasChangedFunction = hasChanged;
  public recentlyResentEmailVerification: boolean = false;
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private resendCooldownTimeout?: ReturnType<typeof setTimeout>;

  public readonly emptyFormGroup: ExtendedFormGroup = this.formBuilder.nonNullable.extendedGroup({});
  public readonly formGroup: MxSelector<ExtendedFormGroup> = createSelector(
    this.currentPatronSelectors.myProfileEditValue,
    (profileEditValue) =>
      buildFormGroup(
        profileEditValue,
        this.validators,
        this.formBuilder
      )
  );

  public ngOnDestroy(): void {
    clearTimeout(this.resendCooldownTimeout);
  }

  public onClickSaveButton(formGroup: ExtendedFormGroup): void {
    if (formGroup.valid) {
      this.store.dispatch(PatronActions.editPatron(formGroup.value));
    }
  }

  public onClickChangeEmail(): void {
    this.store.dispatch(PatronActions.showChangePatronEmail());
  }

  public onClickResendEmailVerification(): void {
    if (this.recentlyResentEmailVerification) return;

    this.recentlyResentEmailVerification = true;
    this.store.dispatch(PatronActions.resendPatronEmailChangeVerification());
    this.resendCooldownTimeout = setTimeout(() => {
      this.recentlyResentEmailVerification = false;
      this.changeDetector.markForCheck();
    }, VERIFICATION_EMAIL_RESEND_COOLDOWN_MS);
  }

  public onClickCancelEmailChange(): void {
    this.store.dispatch(PatronActions.showCancelPatronEmailChange());
  }

  public onClickChangePassword(): void {
    this.store.dispatch(PatronActions.showChangePassword());
  }

  public onClickChangePIN(): void {
    this.store.dispatch(PatronActions.showChangePin());
  }

  protected readonly EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS = EXTENDED_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS;
  protected readonly STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS = STANDARD_PATRON_NOTIFICATION_PREFERENCE_SELECT_OPTIONS;
}
